import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { CalendarClock, Pause, Play, Trash2 } from 'lucide-react'
import {
  deleteSpotcheckSchedule,
  fetchSpotcheckSchedules,
  pauseSpotcheckSchedule,
  resumeSpotcheckSchedule,
  type SpotcheckScheduleDto,
} from '../services/complianceV2'
import { MimCard, MimPage, StatusPill, formatDateTime } from '../components/MimUi'

export default function Scheduler() {
  const qc = useQueryClient()
  const { data: schedules = [], isLoading } = useQuery({
    queryKey: ['spotcheck-schedules'],
    queryFn: fetchSpotcheckSchedules,
  })

  const pauseMut = useMutation({
    mutationFn: (id: string) => pauseSpotcheckSchedule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spotcheck-schedules'] }),
  })
  const resumeMut = useMutation({
    mutationFn: (id: string) => resumeSpotcheckSchedule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spotcheck-schedules'] }),
  })
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteSpotcheckSchedule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['spotcheck-schedules'] }),
  })

  return (
    <MimPage
      title="Check Scheduler"
      subtitle="Recurring website compliance spot checks. Completed runs email a DOCX report to the schedule owner."
    >
      <div className="mim-stack">
        <MimCard>
          <p className="mim-help-text" style={{ margin: 0 }}>
            Create schedules from the Website Check page using <strong>Add to scheduler</strong>. Schedules queue one v2 spotcheck batch per occurrence.
          </p>
        </MimCard>

        <MimCard title={`Schedules (${schedules.length})`}>
          {isLoading && <p>Loading schedules…</p>}
          {!isLoading && schedules.length === 0 && (
            <p className="mim-help-text">No schedules yet. Open Website Check, configure products/URLs, then choose Add to scheduler.</p>
          )}
          {schedules.length > 0 && (
            <div className="mim-table-wrap">
              <table className="mim-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Owner</th>
                    <th>Cadence</th>
                    <th>Products</th>
                    <th>Policy pack</th>
                    <th>Last run</th>
                    <th>Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {schedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <CalendarClock size={14} />
                          <strong>{schedule.name}</strong>
                        </div>
                        {schedule.lastError && <div className="mim-help-text" style={{ color: '#991b1b' }}>{schedule.lastError}</div>}
                      </td>
                      <td>{schedule.ownerEmail}</td>
                      <td>{formatCadence(schedule)}</td>
                      <td>{schedule.productIds.length}</td>
                      <td>{schedule.rulePackName || 'Core only'}</td>
                      <td>
                        {schedule.lastRunUtc ? formatDateTime(schedule.lastRunUtc) : '—'}
                        {schedule.lastBatchId && (
                          <div><Link to="/check-history">Batch {schedule.lastBatchId.slice(0, 8)}</Link></div>
                        )}
                      </td>
                      <td><StatusPill status={schedule.isActive ? 'Active' : 'Paused'} tone={schedule.isActive ? 'pass' : 'warning'} /></td>
                      <td>
                        <div className="mim-toolbar">
                          {schedule.isActive ? (
                            <button type="button" className="mim-btn mim-btn-secondary" onClick={() => pauseMut.mutate(schedule.id)} disabled={pauseMut.isPending}>
                              <Pause size={14} /> Pause
                            </button>
                          ) : (
                            <button type="button" className="mim-btn mim-btn-secondary" onClick={() => resumeMut.mutate(schedule.id)} disabled={resumeMut.isPending}>
                              <Play size={14} /> Resume
                            </button>
                          )}
                          <button
                            type="button"
                            className="mim-btn mim-btn-danger"
                            onClick={() => {
                              if (window.confirm(`Delete schedule "${schedule.name}"?`)) deleteMut.mutate(schedule.id)
                            }}
                            disabled={deleteMut.isPending}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </MimCard>
      </div>
    </MimPage>
  )
}

function formatCadence(schedule: SpotcheckScheduleDto) {
  const time = `${String(schedule.hourUtc).padStart(2, '0')}:${String(schedule.minuteUtc).padStart(2, '0')} UTC`
  if (schedule.frequency === 'Daily') return `Daily at ${time}`
  if (schedule.frequency === 'Monthly') return `Monthly on day ${schedule.dayOfMonth ?? 1} at ${time}`
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  return `Weekly on ${days[schedule.dayOfWeek ?? 1]} at ${time}`
}
